import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "@/hooks/use-toast";
import { oauthApi } from "@/lib/oauthApi";
import { authToken } from "@/lib/api";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get the access token from URL params (Supabase OAuth)
        const accessToken = searchParams.get("access_token");

        if (accessToken) {
          // Handle Supabase OAuth callback
          const response = await oauthApi.handleSupabaseCallback(accessToken);

          // Store the JWT token
          authToken.set(response.access_token);

          setStatus("success");

          toast({
            title: "Welcome! 👋",
            description: `Successfully logged in as ${response.user.role.toLowerCase()}.`,
          });

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate("/");
          }, 1500);
        } else {
          // Handle regular OAuth callback with code
          const code = searchParams.get("code");

          if (!code) {
            throw new Error("No authorization code or access token found");
          }

          const response = await oauthApi.handleGoogleCallback(code);

          // Store the JWT token
          authToken.set(response.access_token);

          setStatus("success");

          toast({
            title: "Welcome! 👋",
            description: `Successfully logged in as ${response.user.role.toLowerCase()}.`,
          });

          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate("/");
          }, 1500);
        }
      } catch (error) {
        console.error("OAuth callback error:", error);
        setStatus("error");
        setError(
          error instanceof Error ? error.message : "Authentication failed"
        );

        toast({
          title: "Authentication failed",
          description:
            error instanceof Error
              ? error.message
              : "Failed to complete authentication",
          variant: "destructive",
        });

        // Redirect to login after error
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-auto p-8"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === "loading" && (
            <>
              <div className="mb-6">
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Completing Authentication
              </h2>
              <p className="text-slate-600">
                Please wait while we complete your login...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mb-6">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Authentication Successful!
              </h2>
              <p className="text-slate-600">
                Redirecting you to the dashboard...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="mb-6">
                <XCircle className="w-12 h-12 text-red-500 mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Authentication Failed
              </h2>
              <p className="text-slate-600 mb-4">
                {error || "Something went wrong during authentication."}
              </p>
              <p className="text-sm text-slate-500">
                Redirecting you back to login...
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AuthCallback;
